import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { Mail, ShieldQuestion, Loader2 } from "lucide-react";
import AuthSplitLayout from "../../components/auth/AuthSplitLayout";
import { useSelector } from "react-redux";
import { requestConsumerForgotPassword, getErrorMessage } from "../../api/userAuthApi";

import showToast from "../../utils/toast";

const inputClassName =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#34846E] focus:ring-4 focus:ring-[#34846E]/15";

const ForgotPassword = (props = {}) => {
  const {
    embed = false,
    onBackToSignIn,
    prefillEmail: embedPrefillEmail = "",
  } = props;

  useEffect(() => {
    if (embed) return;
    window.scrollTo(0, 0);
  }, [embed]);

  const user = useSelector((state) => state.infoboxx_consumer.user);

  const [email, setEmail] = useState(embedPrefillEmail || "");
  const [showModal, setShowModal] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (embedPrefillEmail) setEmail(String(embedPrefillEmail).trim());
  }, [embedPrefillEmail]);

  const handlesubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const emailToUse = user?.email || email.trim();

    if (!emailToUse) {
      showToast.warning("Enter your email to continue.");
      return;
    }

    setIsSubmitting(true);
    try {
      await requestConsumerForgotPassword({ email: emailToUse, user_type: "user" });

      showToast.success(`Password reset link has been sent to ${emailToUse}`);

      setSentEmail(emailToUse);
      setShowModal(true);

      setEmail("");
    } catch (err) {
      const message = getErrorMessage(err);
      showToast.error(typeof message === "string" ? message : "Failed to send reset link. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal Component
  const SuccessModal = () => {
    if (!showModal) return null;

    return (
      <div
        className={`fixed inset-0 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm ${embed ? "z-[180]" : "z-50"}`}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-yellow-500"></div>

          <div className="p-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                <Mail className="text-yellow-600" size={32} />
              </div>
            </div>

            <h3 className="text-xl font-bold text-center text-slate-900 mb-2">
              Check your email!
            </h3>

            <p className="text-center text-slate-600 mb-4">
              We've sent a password reset link to:
            </p>

            <p className="text-center font-semibold text-amber-700 bg-amber-50 py-2 px-3 rounded-lg mb-4 break-all">
              {sentEmail}
            </p>

            <p className="text-sm text-center text-slate-500 mb-6">
              Click the link in the email to reset your password. The link will expire in 1 hour for security reasons.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => setShowModal(false)}
                className="w-full rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 px-4 py-3 text-sm font-semibold text-black shadow-lg shadow-yellow-900/20 transition hover:from-yellow-600 hover:to-amber-600 hover:shadow-yellow-900/30"
              >
                Got it, thanks
              </button>

              {embed ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    onBackToSignIn?.();
                  }}
                  className="block w-full text-center rounded-xl border-2 border-yellow-500 bg-white px-4 py-3 text-sm font-semibold text-yellow-700 transition hover:bg-yellow-50 hover:border-yellow-600 hover:text-yellow-800"
                >
                  Return to sign in
                </button>
              ) : (
                <Link
                  to="/signin"
                  className="block w-full text-center rounded-xl border-2 border-yellow-500 bg-white px-4 py-3 text-sm font-semibold text-yellow-700 transition hover:bg-yellow-50 hover:border-yellow-600 hover:text-yellow-800"
                >
                  Return to sign in
                </Link>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  const forgotForm = (
        <form className="space-y-4" onSubmit={handlesubmit}>
          <label className="space-y-1.5">
            <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">Registered email</span>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-amber-500" size={17} />
              <input
                className={`pl-10 ${inputClassName} focus:border-amber-400 focus:ring-4 focus:ring-amber-400/15`}
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!user?.email || isSubmitting}
                required={!user?.email}
              />
            </div>
          </label>

          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 text-sm text-amber-800">
            <p className="inline-flex gap-2 items-start">
              <ShieldQuestion size={18} className="mt-0.5 shrink-0 text-amber-600" />
              We will send a reset link to your inbox. Click the link to reset your password.
            </p>
          </div>

          <motion.button
            whileHover={
              isSubmitting
                ? undefined
                : { scale: 1.02, boxShadow: "0 10px 25px -5px rgba(245, 158, 11, 0.3)" }
            }
            whileTap={isSubmitting ? undefined : { scale: 0.98 }}
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 px-4 py-3 text-sm font-bold text-black shadow-lg shadow-yellow-900/30 transition-all duration-200 hover:from-yellow-600 hover:to-amber-600 hover:shadow-yellow-900/40 hover:text-black disabled:pointer-events-none disabled:opacity-70 disabled:hover:from-yellow-500 disabled:hover:to-amber-500"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin shrink-0" aria-hidden />
                Sending…
              </>
            ) : (
              "Send reset link"
            )}
          </motion.button>
        </form>
  );

  return (
    <>
      <SuccessModal />
      {embed ? (
        <div className="space-y-5 sm:space-y-6">
          {forgotForm}
          <p className="text-center text-sm text-slate-600">
            Remember your password?{" "}
            <button
              type="button"
              onClick={() => onBackToSignIn?.()}
              className="font-semibold text-amber-700 underline-offset-2 hover:text-amber-900"
            >
              Back to sign in
            </button>
          </p>
        </div>
      ) : (
      <AuthSplitLayout
        title="Forgot your password?"
        subtitle="No stress. Enter your registered email and we will send a password reset link."
        sideBadge="ACCOUNT RECOVERY"
        sideTitle="Recover access quickly and securely."
        sideDescription="Your account security stays protected while giving you a simple way to reset password."
        sideBullets={[
          "Secure reset link sent to email",
          "Quick guided recovery flow",
        ]}
        footer={
          <p className="text-slate-600">
            Remember your password?{" "}
            <Link to="/signin" className="font-semibold text-amber-600 hover:text-amber-700 border-b border-amber-200 hover:border-amber-400 transition">
              Return to sign in
            </Link>
          </p>
        }
      >
        {forgotForm}
      </AuthSplitLayout>
      )}
    </>
  );
};

export default ForgotPassword;